import type {
  SmartCreationBrowserDraft,
  SmartCreationStructure,
  SmartCreationStructureEvaluation,
  SmartCreationStructureRepairAction,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { RecommendationField } from "@/components/smart-creation/ObjectivesStep";
import { writeLocalSmartCreationEditedPlan } from "@/lib/smart-creation-draft-store";
import {
  formatCurrencyValue,
  hasValidCampaignWindow,
  hasValidPromotionWindow,
  minimumTotalDailyBudget,
  respectsDecimalPrecision,
  STEP_ORDER,
} from "./draft-rules";
import type { PlanSession } from "./use-plan-session";
import type { StepInputs } from "./use-step-inputs";
import type { WorkspaceMutations } from "./use-workspace-mutations";

interface StepCommandsOptions {
  session: PlanSession;
  stepInputs: StepInputs;
  mutations: WorkspaceMutations;
  canEdit: boolean;
  selectedStructureEvaluation: SmartCreationStructureEvaluation | undefined;
}

/**
 * Wizard navigation and the per-step commands of the Products, Objectives and Structure
 * steps. Step changes validate locally first, then save through the serialized action lock.
 */
export function useStepCommands({
  session,
  stepInputs,
  mutations,
  canEdit,
  selectedStructureEvaluation,
}: StepCommandsOptions) {
  const { t } = useTranslation("smartCreation");
  const {
    actionLock,
    awaitingGeneratePreview,
    browserDraftRef,
    currentIdentityRef,
    currentStep,
    draft,
    draftRef,
    editVersionRef,
    mountedRef,
    plan,
    releaseActionLock,
    structureQueueRef,
  } = session;
  const capabilities = session.capabilitiesQuery.data;
  const { beginAction, generatePreviewMutation, recommendationMutation, stepMutation } = mutations;
  const currentStepIndex = STEP_ORDER.indexOf(currentStep);

  const selectStructure = (structure: SmartCreationStructure) => {
    structureQueueRef.current?.select(structure);
  };

  const flushStructureAction = async (blockContent = true) => {
    const identity = beginAction({ blockContent, flushStructure: true });
    if (!identity) return null;
    if (
      !(await structureQueueRef.current!.flush()) ||
      !mountedRef.current ||
      identity !== currentIdentityRef.current
    ) {
      releaseActionLock(identity);
      return null;
    }
    return identity;
  };

  const repairStructure = async (repair: SmartCreationStructureRepairAction) => {
    const currentPlan = browserDraftRef.current;
    const currentDraft = draftRef.current;
    if (currentPlan?.status !== "draft" || !currentDraft) return;
    if (repair.action === "switch_structure") {
      selectStructure(repair.structure);
      return;
    }

    const identity = await flushStructureAction();
    if (!identity) return;
    const savedPlan = browserDraftRef.current!;
    const savedDraft = draftRef.current!;
    const budget =
      repair.action === "increase_budget"
        ? repair.minimumTotalDailyBudget
        : repair.action === "decrease_budget"
          ? repair.maximumTotalDailyBudget
          : null;
    const nextDraft =
      budget !== null
        ? { ...savedDraft, totalDailyBudget: budget }
        : {
            ...savedDraft,
            currentStep:
              repair.action === "reduce_strategies" || repair.action === "reduce_targets"
                ? ("strategy" as const)
                : ("products" as const),
          };
    stepMutation.mutate({
      identity,
      currentPlan: savedPlan,
      nextDraft,
    });
  };

  // Another tab can cancel or submit this plan, which makes the local write throw.
  const writeStepLocally = (next: SmartCreationBrowserDraft, identity: string) => {
    try {
      if (!writeLocalSmartCreationEditedPlan(next)) toast.warning(t("errors.localStorage"));
      session.setPlan(next);
    } catch {
      toast.error(t("errors.action"));
    } finally {
      releaseActionLock(identity);
    }
  };

  const goBack = async () => {
    const currentPlan = browserDraftRef.current;
    const currentDraft = draftRef.current;
    if (!currentDraft || !currentPlan || currentStepIndex <= 0) return;
    const identity = await flushStructureAction();
    if (!identity) return;
    const saved = browserDraftRef.current!;
    if (currentStep === "preview") {
      const next = { ...saved, draft: { ...saved.draft, currentStep: "structure" as const } };
      writeStepLocally(next, identity);
      return;
    }
    stepMutation.mutate({
      identity,
      currentPlan: saved,
      nextDraft: { ...draftRef.current!, currentStep: STEP_ORDER[currentStepIndex - 1]! },
    });
  };

  const goNext = async () => {
    if (!draft || !plan || !canEdit) return;
    if (currentStep === "products" && (draft.products.length === 0 || stepInputs.catalogBlocked)) {
      stepInputs.setValidationError(t("errors.product"));
      return;
    }
    if (
      currentStep === "objectives" &&
      (!draft.objective || !draft.targetAcos || !draft.totalDailyBudget)
    ) {
      stepInputs.setValidationError(t("errors.objective"));
      return;
    }
    if (currentStep === "objectives" && !hasValidCampaignWindow(draft)) {
      stepInputs.setValidationError(t("errors.campaignWindow"));
      return;
    }
    if (currentStep === "objectives" && !hasValidPromotionWindow(draft)) {
      stepInputs.setPromotionValidationRequested(true);
      return;
    }
    if (currentStep === "objectives" && capabilities && draft.totalDailyBudget) {
      const minimumBudget = minimumTotalDailyBudget(draft, capabilities);
      if (
        draft.totalDailyBudget < minimumBudget ||
        !respectsDecimalPrecision(draft.totalDailyBudget, capabilities.dailyBudget.decimals)
      ) {
        stepInputs.setValidationError(
          t("errors.budget", {
            minimum: formatCurrencyValue(
              minimumBudget,
              capabilities.currencyCode,
              capabilities.dailyBudget.decimals,
            ),
            decimals: capabilities.dailyBudget.decimals,
          }),
        );
        return;
      }
    }
    if (currentStep === "structure" && !selectedStructureEvaluation?.feasible) {
      stepInputs.setValidationError(t("errors.structure"));
      return;
    }
    let currentPlan = browserDraftRef.current ?? plan;
    if (currentStep === "preview" && !currentPlan.reviewWorkspace) {
      stepInputs.setValidationError(t("errors.action"));
      return;
    }
    const identity = await flushStructureAction();
    if (!identity) return;
    currentPlan = browserDraftRef.current ?? currentPlan;
    const savedDraft = draftRef.current ?? draft;

    if (currentStep === "products") {
      stepMutation.mutate({
        identity,
        currentPlan,
        nextDraft: { ...savedDraft, currentStep: "objectives" },
      });
      return;
    }
    if (currentStep === "objectives") {
      stepMutation.mutate({
        identity,
        currentPlan,
        nextDraft: { ...savedDraft, currentStep: "strategy" },
      });
      return;
    }
    if (currentStep === "strategy") {
      stepMutation.mutate({
        identity,
        currentPlan,
        nextDraft: { ...savedDraft, currentStep: "structure" },
      });
      return;
    }
    if (currentStep === "structure") {
      if (
        currentPlan.status === "preview_ready" &&
        currentPlan.previewForRevision === currentPlan.revision &&
        currentPlan.reviewWorkspace?.basedOn.planRevision === currentPlan.revision
      ) {
        const next = {
          ...currentPlan,
          draft: { ...currentPlan.draft, currentStep: "preview" as const },
        };
        writeStepLocally(next, identity);
        return;
      }
      generatePreviewMutation.mutate({
        identity,
        currentPlan,
        nextDraft: { ...savedDraft, currentStep: "preview" },
        stepAlreadySaved: awaitingGeneratePreview,
      });
      return;
    }
    releaseActionLock(identity);
  };

  const refreshRecommendation = () => {
    const currentPlan = browserDraftRef.current;
    const currentDraft = draftRef.current;
    if (!currentPlan || !currentDraft?.objective) {
      stepInputs.setValidationError(t("errors.recommendationContext"));
      return;
    }
    if (!hasValidCampaignWindow(currentDraft)) {
      stepInputs.setValidationError(t("errors.campaignWindow"));
      return;
    }
    if (!hasValidPromotionWindow(currentDraft)) {
      stepInputs.setPromotionValidationRequested(true);
      return;
    }
    if (actionLock.current || recommendationMutation.isPending) return;
    const identity = currentIdentityRef.current;
    recommendationMutation.reset();
    recommendationMutation.mutate({
      identity,
      editVersion: editVersionRef.current,
      currentPlan,
      initialDraft: currentDraft,
    });
  };

  const applyRecommendation = (field: RecommendationField) => {
    const currentDraft = draftRef.current;
    const recommendation = stepInputs.objectiveRecommendation;
    if (!currentDraft || !recommendation) {
      toast.error(t("errors.recommendationUnavailable"));
      return;
    }
    if (
      field === "totalDailyBudget" &&
      recommendation.totalDailyBudget.source === "capability_floor"
    ) {
      toast.error(t("objectives.noHistoricalBudgetRecommendation"));
      return;
    }
    stepInputs.changeLocalDraft(
      field === "targetAcos"
        ? {
            ...currentDraft,
            targetAcos:
              Math.round((recommendation.targetAcos.value + Number.EPSILON) * 10_000) / 10_000,
          }
        : { ...currentDraft, totalDailyBudget: recommendation.totalDailyBudget.value },
      true,
    );
  };

  const reconnectAmazon = () => {
    const currentPlan = browserDraftRef.current;
    if (!currentPlan) return;
    const identity = beginAction();
    if (!identity) return;
    mutations.reconnectMutation.mutate({
      identity,
      currentPlan,
    });
  };

  const cancelPlan = async () => {
    const identity = await flushStructureAction(false);
    const currentPlan = browserDraftRef.current;
    if (identity && currentPlan) mutations.cancelMutation.mutate({ identity, currentPlan });
  };

  return {
    currentStepIndex,
    selectStructure,
    repairStructure,
    goBack,
    goNext,
    refreshRecommendation,
    applyRecommendation,
    reconnectAmazon,
    cancelPlan,
  };
}
